import { apiClient } from "@/lib/api/client";

export type SupportUserRefDto = {
  id: string;
  name: string | null;
  email: string | null;
  role: string | null;
};

export type SupportDeviceRefDto = {
  id: string;
  name: string | null;
  device_uid: string;
};

export type SupportReportUploadDto = {
  path: string;
  file_name: string;
  content_type: string;
};

export type SupportMessageDto = {
  id: string;
  author_user_id: string | null;
  author: string;
  author_role: string | null;
  body: string;
  created_at: string;
};

export type SupportTicketEventDto = {
  id: string;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_role: string | null;
  kind: string;
  title: string;
  detail: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
};

export type SupportAttachmentDto = {
  id: string;
  file_name: string;
  content_type: string;
  created_at: string;
};

export type ExpenseCategoryOptionDto = { id: string; name: string };

export type ExpenseReceiptDto = {
  id: string;
  file_name: string;
  content_type: string;
  created_at: string;
};

export type SupportTicketExpenseDto = {
  id: string;
  ticket_id: string;
  tenant_id: string;
  device_id: string | null;
  category_id: string;
  category_name: string;
  expense_date: string;
  amount: string;
  currency: string;
  purpose: string;
  vendor: string | null;
  payment_method: "cash" | "card" | "bank_transfer" | "upi" | "other" | null;
  reference_number: string | null;
  tax_amount: string | null;
  is_reimbursable: boolean;
  internal_note: string | null;
  incurred_by_user_id: string;
  status: "posted" | "void";
  created_at: string;
  updated_at: string;
  receipts: ExpenseReceiptDto[];
};

export type SupportTicketExpenseBody = {
  category_id: string;
  expense_date: string;
  amount: string;
  currency: string;
  purpose: string;
  vendor?: string | null;
  payment_method?: "cash" | "card" | "bank_transfer" | "upi" | "other" | null;
  reference_number?: string | null;
  tax_amount?: string | null;
  is_reimbursable?: boolean;
  internal_note?: string | null;
};

export type SupportTicketSummaryDto = {
  id: string;
  tenant_id: string | null;
  device_id: string | null;
  tenant_name: string | null;
  opened_by_user_id: string | null;
  assigned_admin_user_id: string | null;
  resolved_by_user_id: string | null;
  subject: string;
  category: string;
  status: "open" | "pending_admin" | "pending_requester" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_reply_at: string | null;
  last_reply_by_role: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  requester_unread_count: number;
  admin_unread_count: number;
  viewer_unread_count: number;
  message_count: number;
  closed_report_path: string | null;
  closed_report_name: string | null;
  closed_report_content_type: string | null;
  device: SupportDeviceRefDto | null;
  opened_by_user: SupportUserRefDto | null;
  assigned_admin_user: SupportUserRefDto | null;
  resolved_by_user: SupportUserRefDto | null;
};

export type SupportTicketDto = SupportTicketSummaryDto & {
  messages: SupportMessageDto[];
  events: SupportTicketEventDto[];
  attachments: SupportAttachmentDto[];
  expenses: SupportTicketExpenseDto[];
};

export type SupportTicketsSummaryDto = {
  total: number;
  open: number;
  pending_admin: number;
  pending_requester: number;
  resolved: number;
  closed: number;
  unassigned: number;
  viewer_unread: number;
};

export type SupportTicketsResponse = {
  tickets: SupportTicketSummaryDto[];
  summary: SupportTicketsSummaryDto;
  total: number;
  limit: number;
  offset: number;
};

export type SupportTicketListParams = {
  tenantId?: string | null;
  search?: string;
  status?: string;
  assignment?: string;
  limit?: number;
  offset?: number;
};

export async function listSupportTickets(params: SupportTicketListParams = {}): Promise<SupportTicketsResponse> {
  const requestParams: Record<string, string | number> = {};
  if (params.tenantId) requestParams.tenant_id = params.tenantId;
  if (params.search?.trim()) requestParams.search = params.search.trim();
  if (params.status) requestParams.status = params.status;
  if (params.assignment) requestParams.assignment = params.assignment;
  if (typeof params.limit === "number") requestParams.limit = params.limit;
  if (typeof params.offset === "number") requestParams.offset = params.offset;
  const { data } = await apiClient.get<SupportTicketsResponse>("/support", {
    params: requestParams,
  });
  return data;
}

export async function getSupportTicket(ticketId: string): Promise<SupportTicketDto> {
  const { data } = await apiClient.get<SupportTicketDto>(`/support/${ticketId}`);
  return data;
}

export type SupportTicketCreateBody = {
  tenant_id?: string | null;
  device_id: string;
  subject: string;
  category: string;
  priority: "low" | "medium" | "high" | "urgent";
  initial_message: string;
  assigned_admin_user_id?: string | null;
};

export type SupportTicketUpdateBody = Partial<{
  subject: string;
  category: string;
  status: "open" | "pending_admin" | "pending_requester" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  assigned_admin_user_id: string | null;
  closed_report_path: string | null;
  closed_report_name: string | null;
  closed_report_content_type: string | null;
}>;

export async function listSupportAvailableDevices(): Promise<SupportDeviceRefDto[]> {
  const { data } = await apiClient.get<SupportDeviceRefDto[]>("/support/available-devices");
  return data;
}

export async function listSupportAssignees(): Promise<SupportUserRefDto[]> {
  const { data } = await apiClient.get<SupportUserRefDto[]>("/support/assignees");
  return data;
}

export async function createSupportTicket(body: SupportTicketCreateBody): Promise<SupportTicketDto> {
  const { data } = await apiClient.post<SupportTicketDto>("/support", body);
  return data;
}

export async function updateSupportTicket(ticketId: string, body: SupportTicketUpdateBody): Promise<SupportTicketDto> {
  const { data } = await apiClient.put<SupportTicketDto>(`/support/${ticketId}`, body);
  return data;
}

export async function addSupportTicketMessage(ticketId: string, body: { body: string }): Promise<SupportTicketDto> {
  const { data } = await apiClient.post<SupportTicketDto>(`/support/${ticketId}/messages`, body);
  return data;
}

export async function markSupportTicketRead(ticketId: string): Promise<SupportTicketDto> {
  const { data } = await apiClient.post<SupportTicketDto>(`/support/${ticketId}/read`);
  return data;
}

export async function deleteSupportTicket(ticketId: string): Promise<void> {
  await apiClient.delete(`/support/${ticketId}`);
}

export async function uploadSupportReport(file: File): Promise<SupportReportUploadDto> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<SupportReportUploadDto>("/support/report-upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
}

export async function downloadSupportReport(ticketId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/support/${ticketId}/report`, {
    responseType: "blob",
  });
  return data;
}

export async function uploadSupportTicketAttachment(ticketId: string, file: File): Promise<SupportAttachmentDto> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<SupportAttachmentDto>(`/support/${ticketId}/attachments`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function downloadSupportTicketAttachment(ticketId: string, attachmentId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/support/${ticketId}/attachments/${attachmentId}`, { responseType: "blob" });
  return data;
}

export async function listSupportExpenseCategories(): Promise<ExpenseCategoryOptionDto[]> {
  const { data } = await apiClient.get<ExpenseCategoryOptionDto[]>("/support/expense-categories");
  return data;
}

export async function createSupportTicketExpense(ticketId: string, body: SupportTicketExpenseBody): Promise<SupportTicketExpenseDto> {
  const { data } = await apiClient.post<SupportTicketExpenseDto>(`/support/${ticketId}/expenses`, body);
  return data;
}

export async function updateSupportTicketExpense(ticketId: string, expenseId: string, body: SupportTicketExpenseBody & { status: "posted" | "void" }): Promise<SupportTicketExpenseDto> {
  const { data } = await apiClient.put<SupportTicketExpenseDto>(`/support/${ticketId}/expenses/${expenseId}`, body);
  return data;
}

export async function uploadExpenseReceipt(ticketId: string, expenseId: string, file: File): Promise<ExpenseReceiptDto> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<ExpenseReceiptDto>(`/support/${ticketId}/expenses/${expenseId}/receipts`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export async function downloadExpenseReceipt(ticketId: string, expenseId: string, receiptId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/support/${ticketId}/expenses/${expenseId}/receipts/${receiptId}`, { responseType: "blob" });
  return data;
}
